import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { ChatCryptoBootstrapProvider } from './contexts/ChatCryptoBootstrapContext';
import { ChatInboxUnreadProvider } from './contexts/ChatInboxUnreadContext';
import { ThemeProvider } from './components/theme-provider';
import { ThemeSchedule } from './components/theme-schedule';
import { GlobalIncomingCallBanner } from './components/chats/GlobalIncomingCallBanner';
import { MessengerAppPresenceHeartbeat } from './components/MessengerAppPresenceHeartbeat';
import { ProtectedRoute } from './components/ProtectedRoute';
import { AppPinLockProvider } from './contexts/AppPinLockContext';
import { RouteFallback } from './components/RouteFallback';
import { Toaster } from './components/ui/sonner';

const AboutPage = lazy(() =>
  import('./components/AboutPage').then((m) => ({ default: m.AboutPage }))
);
const SettingsPage = lazy(() =>
  import('./components/SettingsPage').then((m) => ({ default: m.SettingsPage }))
);
const AccountPlaceholderPage = lazy(() =>
  import('./components/AppPlaceholderPage').then((m) => ({ default: m.AccountPlaceholderPage }))
);
const BillingPlaceholderPage = lazy(() =>
  import('./components/AppPlaceholderPage').then((m) => ({ default: m.BillingPlaceholderPage }))
);
const NotificationsPlaceholderPage = lazy(() =>
  import('./components/AppPlaceholderPage').then((m) => ({ default: m.NotificationsPlaceholderPage }))
);
const UpgradePage = lazy(() =>
  import('./components/UpgradePage').then((m) => ({ default: m.UpgradePage }))
);
const PeoplePage = lazy(() =>
  import('./components/people/PeoplePage').then((m) => ({ default: m.PeoplePage }))
);
const ChatsPage = lazy(() =>
  import('./components/chats/ChatsPage').then((m) => ({ default: m.ChatsPage }))
);
const MeetHostPage = lazy(() =>
  import('./components/meet/MeetPages').then((m) => ({ default: m.MeetHostPage }))
);
const MeetJoinPage = lazy(() =>
  import('./components/meet/MeetPages').then((m) => ({ default: m.MeetJoinPage }))
);
const VerifyEmailRoute = lazy(() =>
  import('./components/VerifyEmailRoute').then((m) => ({ default: m.VerifyEmailRoute }))
);
const ConfirmDisplayNamePage = lazy(() =>
  import('./components/ConfirmDisplayNamePage').then((m) => ({ default: m.ConfirmDisplayNamePage }))
);
const RetroLandingPage = lazy(() =>
  import('./components/landing/RetroLandingPage').then((m) => ({ default: m.RetroLandingPage }))
);
const ComponentsShowcase = lazy(() => import('./components/ComponentsShowcase'));
const NotFoundPage = lazy(() =>
  import('./components/NotFoundPage').then((m) => ({ default: m.NotFoundPage }))
);
const LegalTermsPage = lazy(() =>
  import('./components/legal/LegalStaticPages').then((m) => ({ default: m.LegalTermsPage }))
);
const LegalPrivacyPage = lazy(() =>
  import('./components/legal/LegalStaticPages').then((m) => ({ default: m.LegalPrivacyPage }))
);

function LandingPage() {
  return <RetroLandingPage />;
}

function App() {
  return (
    <ThemeProvider attribute="class" defaultTheme="system" enableSystem>
      <ThemeSchedule />
      <Toaster position="top-center" />
      <AuthProvider>
        <AppPinLockProvider>
          <Router
            future={{
              v7_startTransition: true,
              v7_relativeSplatPath: true,
            }}
          >
          <MessengerAppPresenceHeartbeat />
          <ChatCryptoBootstrapProvider>
            <ChatInboxUnreadProvider>
              <GlobalIncomingCallBanner />
              <Suspense fallback={<RouteFallback />}>
                <Routes>
                  <Route path="/" element={<LandingPage />} />
                  <Route path="/verify-email" element={<VerifyEmailRoute />} />
                  <Route
                    path="/confirm-display-name"
                    element={
                      <ProtectedRoute>
                        <ConfirmDisplayNamePage />
                      </ProtectedRoute>
                    }
                  />
                  <Route path="/legal/terms" element={<LegalTermsPage />} />
                  <Route path="/legal/privacy" element={<LegalPrivacyPage />} />
                  <Route path="/components" element={<ComponentsShowcase />} />

                  <Route
                    path="/app"
                    element={
                      <ProtectedRoute>
                        <Navigate to="/app/chats" replace />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/app/chats"
                    element={
                      <ProtectedRoute>
                        <ChatsPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/app/chats/:chatId"
                    element={
                      <ProtectedRoute>
                        <ChatsPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/app/presentations/*"
                    element={
                      <ProtectedRoute>
                        <Navigate to="/app/chats" replace />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/app/meet"
                    element={
                      <ProtectedRoute>
                        <MeetHostPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/app/meet/:roomId"
                    element={
                      <ProtectedRoute>
                        <MeetHostPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route path="/meet/:roomId" element={<MeetJoinPage />} />
                  <Route
                    path="/app/people"
                    element={
                      <ProtectedRoute>
                        <PeoplePage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/app/about"
                    element={
                      <ProtectedRoute>
                        <AboutPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/app/settings"
                    element={
                      <ProtectedRoute>
                        <SettingsPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/app/account"
                    element={
                      <ProtectedRoute>
                        <AccountPlaceholderPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/app/billing"
                    element={
                      <ProtectedRoute>
                        <BillingPlaceholderPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/app/notifications"
                    element={
                      <ProtectedRoute>
                        <NotificationsPlaceholderPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/app/upgrade"
                    element={
                      <ProtectedRoute>
                        <UpgradePage />
                      </ProtectedRoute>
                    }
                  />

                  <Route path="*" element={<NotFoundPage />} />
                </Routes>
              </Suspense>
            </ChatInboxUnreadProvider>
          </ChatCryptoBootstrapProvider>
          </Router>
        </AppPinLockProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
